import api from './api';
import { ProjectResponse, ProjectListResponse, ProjectUploadRequest } from '../types';

export const projectsService = {
  async getProjects(params?: {
    search?: string;
    project_type?: string;
    min_score?: number;
    max_score?: number;
  }): Promise<ProjectListResponse> {
    const response = await api.get<ProjectListResponse>('/projects', { params });
    return response.data;
  },

  async getProject(projectId: number): Promise<ProjectResponse> {
    const response = await api.get<ProjectResponse>(`/projects/${projectId}`);
    return response.data;
  },

  async uploadProject(
    file: File,
    uploadData: ProjectUploadRequest
  ): Promise<{ project_id: number; message: string; task_id?: string; status?: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('format_type', uploadData.format_type);
    formData.append('report_language', uploadData.report_language);
    formData.append('generate_report', String(uploadData.generate_report));
    formData.append('generate_character_breakdown', String(uploadData.generate_character_breakdown));
    formData.append('generate_bible', String(uploadData.generate_bible));
    formData.append('generate_scene_table', String(uploadData.generate_scene_table));
    formData.append('generate_sequential_breakdown', String(uploadData.generate_sequential_breakdown));
    formData.append('generate_location_breakdown', String(uploadData.generate_location_breakdown || false));

    const response = await api.post<{ project_id: number; message: string; task_id?: string; status?: string }>(
      '/projects/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 900000, // 15 dakika timeout (PDF yükleme ve analiz çok uzun sürebilir)
      }
    );
    return response.data;
  },

  async updateProject(projectId: number, notes: string): Promise<ProjectResponse> {
    const response = await api.patch<ProjectResponse>(`/projects/${projectId}`, {
      user_notes: notes,
    });
    return response.data;
  },

  async toggleFavorite(projectId: number): Promise<{ is_favorite: boolean }> {
    const response = await api.post<{ is_favorite: boolean }>(
      `/projects/${projectId}/favorite`
    );
    return response.data;
  },

  async getAnalysis(projectId: number): Promise<import('../types').AnalysisResponse> {
    const response = await api.get<import('../types').AnalysisResponse>(
      `/projects/${projectId}/analysis`,
      {
        timeout: 600000, // 10 dakika timeout
      }
    );
    return response.data;
  },

  async getBible(projectId: number): Promise<import('../types').BibleResponse> {
    const response = await api.get<import('../types').BibleResponse>(
      `/projects/${projectId}/bible`,
      {
        timeout: 600000, // 10 dakika timeout
      }
    );
    return response.data;
  },

  async getCharacterBreakdown(projectId: number): Promise<import('../types').CharacterBreakdownResponse> {
    const response = await api.get<import('../types').CharacterBreakdownResponse>(
      `/projects/${projectId}/character-breakdown`,
      {
        timeout: 600000, // 10 dakika timeout
      }
    );
    return response.data;
  },

  async regenerateAnalysis(projectId: number): Promise<{ project_id: number; message: string; score: number }> {
    const response = await api.post<{ project_id: number; message: string; score: number }>(
      `/projects/${projectId}/regenerate-analysis`,
      {},
      {
        timeout: 600000, // 10 dakika timeout (Analiz oluşturma uzun sürebilir)
      }
    );
    return response.data;
  },

  async generateBible(projectId: number, targetAudience: string = "Genel İzleyici"): Promise<{ project_id: number; message: string; bible_content: string }> {
    const response = await api.post<{ project_id: number; message: string; bible_content: string }>(
      `/projects/${projectId}/generate-bible?target_audience=${encodeURIComponent(targetAudience)}`,
      {},
      {
        timeout: 600000, // 10 dakika timeout (Bible oluşturma uzun sürebilir)
      }
    );
    return response.data;
  },

  async generateCharacterBreakdown(projectId: number): Promise<{ project_id: number; message: string; breakdown_content: string; generate_character_breakdown?: number | boolean }> {
    const response = await api.post<{ project_id: number; message: string; breakdown_content: string; generate_character_breakdown?: number | boolean }>(
      `/projects/${projectId}/generate-character-breakdown`,
      {},
      {
        timeout: 600000, // 10 dakika timeout (Karakter dökümü oluşturma uzun sürebilir)
      }
    );
    return response.data;
  },

  async getLocationBreakdown(projectId: number): Promise<{ project_id: number; breakdown_content: string | null }> {
    const response = await api.get<{ project_id: number; breakdown_content: string | null }>(
      `/projects/${projectId}/location-breakdown`,
      { timeout: 60000 }
    );
    return response.data;
  },

  async generateLocationBreakdown(projectId: number): Promise<{
    project_id: number;
    task_id?: string;
    status?: string;
    message: string;
    breakdown_content?: string;
  }> {
    // Returns 202 Accepted with a task_id; the caller polls
    // getTaskStatus(task_id) until SUCCESS, then fetches the breakdown via
    // getLocationBreakdown(projectId). The legacy synchronous response
    // shape (with breakdown_content) is kept as an optional field for
    // backward compatibility against older backends.
    const response = await api.post<{
      project_id: number;
      task_id?: string;
      status?: string;
      message: string;
      breakdown_content?: string;
    }>(
      `/projects/${projectId}/generate-location-breakdown`,
      {},
      { timeout: 60000 }
    );
    return response.data;
  },

  async getSequentialBreakdown(projectId: number): Promise<{ project_id: number; tsv_content: string | null }> {
    const response = await api.get<{ project_id: number; tsv_content: string | null }>(
      `/projects/${projectId}/sequential-breakdown`,
      { timeout: 60000 }
    );
    return response.data;
  },

  async generateSequentialBreakdown(projectId: number): Promise<{ project_id: number; message: string; tsv_content: string }> {
    const response = await api.post<{ project_id: number; message: string; tsv_content: string }>(
      `/projects/${projectId}/generate-sequential-breakdown`,
      {},
      { timeout: 900000 } // 15 dakika (uzun senaryolarda analiz gecikebilir)
    );
    return response.data;
  },

  async generateSceneTable(projectId: number): Promise<{ project_id: number; message: string; scene_count: number; scenes: any[] }> {
    const response = await api.post<{ project_id: number; message: string; scene_count: number; scenes: any[] }>(
      `/projects/${projectId}/generate-scene-table`,
      {},
      {
        timeout: 300000, // 5 dakika timeout (Sahne tablosu oluşturma uzun sürebilir)
      }
    );
    return response.data;
  },

  async getSceneTable(projectId: number): Promise<{ project_id: number; scene_count: number; scenes: any[] }> {
    const response = await api.get<{ project_id: number; scene_count: number; scenes: any[] }>(
      `/projects/${projectId}/scene-table`,
      {
        timeout: 600000, // 10 dakika timeout
      }
    );
    return response.data;
  },

  async deleteProject(projectId: number): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>(`/projects/${projectId}`);
    return response.data;
  },

  async readScript(projectId: number, onProgress?: (progress: number) => void): Promise<Blob> {
    const response = await api.post(
      `/projects/${projectId}/read-script`,
      {},
      {
        responseType: 'blob',
        timeout: 1800000, // 30 dakika timeout (büyük dosyalar için yeterli süre)
        maxContentLength: Infinity, // Büyük dosyalar için sınırsız
        maxBodyLength: Infinity, // Büyük dosyalar için sınırsız
        onDownloadProgress: (progressEvent) => {
          if (onProgress) {
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              onProgress(percentCompleted);
            } else {
              // Total bilgisi yoksa, sadece yüklenen miktarı göster
              const loadedMB = (progressEvent.loaded / (1024 * 1024)).toFixed(2);
              onProgress(parseFloat(loadedMB)); // MB cinsinden göster
            }
          }
        },
      }
    );
    return response.data;
  },

  async getProjectReadings(projectId: number): Promise<{ readings: Array<{ id: number; voice_id: string; created_at: string; has_audio: boolean; text_content?: string | null }> }> {
    const response = await api.get(`/projects/${projectId}/readings`);
    return response.data;
  },

  getScriptReadingDownloadUrl(projectId: number, voiceId: string): string {
    // Return direct download URL for existing files (uses browser's native download)
    const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1')
      .replace(/(\\[rn])+$/g, '')
      .replace(/[\r\n\t\s]+$/g, '')
      .replace(/^\s+/g, '');
    const token = localStorage.getItem('access_token');
    return `${API_BASE_URL}/projects/${projectId}/read-script/${encodeURIComponent(voiceId)}?token=${token}`;
  },

  async getScriptReadingText(projectId: number, voiceId: string): Promise<{ text: string; voice_id: string; project_id: number }> {
    const response = await api.get(`/projects/${projectId}/read-script/${encodeURIComponent(voiceId)}/text`);
    return response.data;
  },

  async getTaskStatus(taskId: string): Promise<{ state: string; status: string; progress: number; result?: any; error?: string }> {
    const response = await api.get(`/projects/tasks/${taskId}/status`);
    return response.data;
  },

  async markProjectAsViewed(projectId: number): Promise<{ message: string; project_id: number }> {
    const response = await api.patch<{ message: string; project_id: number }>(
      `/projects/${projectId}/mark-viewed`
    );
    return response.data;
  },
};

